import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Switch from "@material-ui/core/Switch";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import { Box, Grid, Paper, Tab, Tabs, Typography } from "@material-ui/core";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import TicketIcon from "@material-ui/icons/ConfirmationNumber";
import GroupIcon from "@material-ui/icons/Group";
import HistoryIcon from "@material-ui/icons/History";
import ForumIcon from "@material-ui/icons/Forum";
import CheckCircleIcon from "@material-ui/icons/CheckCircleOutline";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PlugIcon from "@material-ui/icons/SettingsInputComponent";
import DashboardIcon from "@material-ui/icons/BarChart";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import ContactsIcon from "@material-ui/icons/Contacts";
import CampaignIcon from "@material-ui/icons/Announcement";
import FlowIcon from "@material-ui/icons/AccountTree";
import MoneyIcon from "@material-ui/icons/AttachMoney";

const formatDateForInput = (date) => {
  if (!date) return '';
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  return dateString;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialogPaper: {
    borderRadius: 16,
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("xs")]: {
      width: "calc(100% - 16px)",
      maxWidth: "calc(100% - 16px)",
      height: "calc(100% - 16px)",
      maxHeight: "calc(100% - 16px)",
      margin: 8,
    },
  },
  dialogTitle: {
    padding: theme.spacing(3, 3, 2),
    borderBottom: "1px solid #eef2f7",
  },
  titleText: {
    fontSize: 22,
    fontWeight: 800,
    color: "#111827",
  },
  titleHint: {
    color: "#64748b",
    marginTop: theme.spacing(0.5),
  },
  tabsPaper: {
    borderRadius: 0,
    borderBottom: "1px solid #eef2f7",
    boxShadow: "none",
  },
  dialogContent: {
    padding: theme.spacing(3),
    background: "#f8fafc",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  contentPaper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tabPanel: {
    width: "100%",
  },
  section: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#111827",
    marginBottom: theme.spacing(1.5),
  },
  fieldGrid: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      background: "#fff",
    },
    "& .MuiInputBase-root": {
      minHeight: 44,
    },
  },
  avatarSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  permissionsCategory: {
    marginBottom: theme.spacing(2),
  },
  permissionsCategoryTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0,
    margin: theme.spacing(0, 0, 1),
  },
  permissionCard: {
    height: "100%",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: theme.spacing(1.75),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  permissionInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  permissionIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "#eff6ff",
    color: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  },
  permissionName: {
    fontSize: 14,
    fontWeight: 800,
    color: "#111827",
  },
  permissionDescription: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.35,
    marginTop: 2,
  },
  fixedActions: {
    zIndex: 2,
    flex: "0 0 auto",
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #e5e7eb",
    background: "#fff",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
  },
  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  updateInput: {
    display: "none",
  },
  updateLabel: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textTransform: "uppercase",
    textAlign: "center",
    cursor: "pointer",
    border: "2px solid #ccc",
    borderRadius: "5px",
    minWidth: 160,
    fontWeight: "bold",
    color: "#555",
  },
  errorUpdate: {
    border: "2px solid red",
  },
  errorText: {
    color: "red",
    fontSize: "0.8rem",
    fontWeight: "bold",
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
});


const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "false",
    allowSeeMessagesInPendingTickets: "enabled"
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  // const [allTicket, setAllTicket] = useState("disable");
  const { whatsApps } = useWhatsApps();
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets: data.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
          farewellMessage: data.farewellMessage || "",
          // Formatar a data corretamente
          birthDate: formatDateForInput(data.birthDate)
        }));

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setTab("general");
    setAvatar(null);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const permissionCategories = [
    {
      title: "Atendimento",
      items: [
        {
          name: "allTicket",
          label: i18n.t("userModal.form.allTicket"),
          description: "Permite visualizar tickets de todas as filas disponíveis.",
          icon: TicketIcon,
          enabledValue: "enable",
          disabledValue: "disable"
        },
        {
          name: "allowGroup",
          label: i18n.t("userModal.form.allowGroup"),
          description: "Permite atendimento e visualização de grupos.",
          icon: GroupIcon,
          enabledValue: true,
          disabledValue: false
        },
        {
          name: "allHistoric",
          label: i18n.t("userModal.form.allHistoric"),
          description: "Libera acesso ao histórico completo dos atendimentos.",
          icon: HistoryIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        },
        {
          name: "userClosePendingTicket",
          label: i18n.t("userModal.form.userClosePendingTicket"),
          description: "Permite fechar tickets ainda pendentes.",
          icon: CheckCircleIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        },
        {
          name: "allowSeeMessagesInPendingTickets",
          label: i18n.t("userModal.form.allowSeeMessagesInPendingTickets"),
          description: "Permite ver mensagens de tickets pendentes.",
          icon: VisibilityIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        },
        {
          name: "allowRealTime",
          label: i18n.t("userModal.form.allowRealTime"),
          description: "Libera atualização em tempo real para o usuário.",
          icon: AccessTimeIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Conversas",
      items: [
        {
          name: "allUserChat",
          label: i18n.t("userModal.form.allUserChat"),
          description: "Permite acessar conversas vinculadas a outros usuários.",
          icon: ForumIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Contatos",
      items: [
        {
          name: "showContacts",
          label: i18n.t("userModal.form.showContacts"),
          description: "Exibe o módulo de contatos no sistema.",
          icon: ContactsIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Campanhas",
      items: [
        {
          name: "showCampaign",
          label: i18n.t("userModal.form.showCampaign"),
          description: "Exibe as funcionalidades de campanhas.",
          icon: CampaignIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Fluxos",
      items: [
        {
          name: "showFlow",
          label: i18n.t("userModal.form.showFlow"),
          description: "Exibe as funcionalidades de fluxos.",
          icon: FlowIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Dashboard",
      items: [
        {
          name: "showDashboard",
          label: i18n.t("userModal.form.showDashboard"),
          description: "Exibe dashboards e indicadores.",
          icon: DashboardIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Conexões",
      items: [
        {
          name: "allowConnections",
          label: i18n.t("userModal.form.allowConnections"),
          description: "Permite acessar a área de conexões.",
          icon: PlugIcon,
          enabledValue: "enabled",
          disabledValue: "disabled"
        }
      ]
    },
    {
      title: "Financeiro",
      items: [
        {
          name: "finalizacaoComValorVendaAtiva",
          label: "Finalização com Valor de Venda",
          description: "Habilita finalização de atendimento com valor de venda.",
          icon: MoneyIcon,
          enabledValue: "true",
          disabledValue: "false"
        }
      ]
    }
  ];

  const renderPermissionItem = (permission, values, setFieldValue) => {
    const Icon = permission.icon;
    const checked = values[permission.name] === permission.enabledValue;

    return (
      <Grid item xs={12} md={6} key={permission.name}>
        <div className={classes.permissionCard}>
          <div className={classes.permissionInfo}>
            <div className={classes.permissionIcon}>
              <Icon fontSize="small" />
            </div>
            <Box>
              <Typography className={classes.permissionName}>
                {permission.label}
              </Typography>
              <Typography className={classes.permissionDescription}>
                {permission.description}
              </Typography>
            </Box>
          </div>
          <Switch
            color="primary"
            checked={checked}
            onChange={(event) =>
              setFieldValue(
                permission.name,
                event.target.checked
                  ? permission.enabledValue
                  : permission.disabledValue
              )
            }
          />
        </div>
      </Grid>
    );
  };

  // UserModal/index.js - Função handleSaveUser corrigida
  const handleSaveUser = async (values) => {
    const uploadAvatar = async (userId) => {
      if (!avatar || typeof avatar !== 'object') return null;

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(
          `/users/${userId}/media-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return data.user.profileImage;
      } catch (error) {
        console.error("Erro no upload da imagem:", error);
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      finalizacaoComValorVendaAtiva: values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets: values.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled"
    };

    try {
      let responseData;

      if (userId) {
        // Atualizar usuário existente
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        // Upload da imagem se houver uma nova
        if (avatar && typeof avatar === 'object') {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;

            // Atualizar localStorage se for o usuário logado
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        // Criar novo usuário
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        // Upload da imagem se houver
        if (avatar && typeof avatar === 'object' && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      // Recarregar página se for o usuário logado para atualizar a interface
      if (userId === loggedInUser.id) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          <Typography className={classes.titleText}>
            {userId ? "Editar usuário" : "Adicionar usuário"}
          </Typography>
          <Typography className={classes.titleHint}>
            Configure dados de acesso, atendimento e permissões do usuário.
          </Typography>
        </DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form className={classes.form}>
              <Paper className={classes.tabsPaper} elevation={0}>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                >
                  <Tab
                    label={i18n.t("userModal.tabs.general")}
                    value={"general"}
                  />
                  <Tab
                    label={i18n.t("userModal.tabs.permissions")}
                    value={"permissions"}
                  />
                </Tabs>
              </Paper>
              <Paper elevation={0} className={classes.contentPaper}>
                <DialogContent className={classes.dialogContent}>
                  <TabPanel
                    className={classes.tabPanel}
                    value={tab}
                    name={"general"}
                  >
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>Perfil</Typography>
                      <div className={classes.avatarSection}>
                        <AvatarUploader
                          setAvatar={setAvatar}
                          avatar={user.profileImage}
                          companyId={user.companyId}
                        />
                        {user.profileImage && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              user.profileImage = null;
                              setFieldValue("profileImage", null);
                              setAvatar(null);
                            }}
                          >
                            {i18n.t("userModal.title.removeImage")}
                          </Button>
                        )}
                      </div>
                      <Grid container spacing={2} className={classes.fieldGrid}>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.email")}
                            name="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.password")}
                            type="password"
                            name="password"
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <FormControl variant="outlined" margin="dense" fullWidth>
                                <InputLabel id="profile-selection-input-label">
                                  {i18n.t("userModal.form.profile")}
                                </InputLabel>
                                <Field
                                  as={Select}
                                  label={i18n.t("userModal.form.profile")}
                                  name="profile"
                                  labelId="profile-selection-label"
                                  id="profile-selection"
                                  required
                                >
                                  <MenuItem value="admin">Admin</MenuItem>
                                  <MenuItem value="user">User</MenuItem>
                                </Field>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </div>

                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>Atendimento</Typography>
                      <Grid container spacing={2} className={classes.fieldGrid}>
                        <Grid item xs={12}>
                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editQueues"
                            yes={() => (
                              <QueueSelect
                                selectedQueueIds={selectedQueueIds}
                                onChange={(values) => setSelectedQueueIds(values)}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <FormControl variant="outlined" margin="dense" fullWidth>
                                <InputLabel>
                                  {i18n.t("userModal.form.whatsapp")}
                                </InputLabel>
                                <Field
                                  as={Select}
                                  value={whatsappId}
                                  onChange={(e) => setWhatsappId(e.target.value)}
                                  label={i18n.t("userModal.form.whatsapp")}
                                >
                                  <MenuItem value={""}>&nbsp;</MenuItem>
                                  {whatsApps.map((whatsapp) => (
                                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                      {whatsapp.name}
                                    </MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl variant="outlined" margin="dense" fullWidth>
                            <InputLabel>{i18n.t("userModal.form.defaultMenu")}</InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.defaultMenu")}
                              name="defaultMenu"
                              required
                            >
                              <MenuItem value={"open"}>
                                {i18n.t("userModal.form.defaultMenuOpen")}
                              </MenuItem>
                              <MenuItem value={"closed"}>
                                {i18n.t("userModal.form.defaultMenuClosed")}
                              </MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl variant="outlined" margin="dense" fullWidth>
                            <InputLabel>{i18n.t("userModal.form.defaultTheme")}</InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.defaultTheme")}
                              name="defaultTheme"
                              required
                            >
                              <MenuItem value="light">
                                {i18n.t("userModal.form.defaultThemeLight")}
                              </MenuItem>
                              <MenuItem value="dark">
                                {i18n.t("userModal.form.defaultThemeDark")}
                              </MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </div>

                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>Horário e dados complementares</Typography>
                      <Grid container spacing={2} className={classes.fieldGrid}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editProfile"
                          yes={() => (
                            <>
                              <Grid item xs={12} md={6}>
                                <Field
                                  as={TextField}
                                  label={i18n.t("userModal.form.startWork")}
                                  type="time"
                                  ampm={"false"}
                                  inputRef={startWorkRef}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 600 }}
                                  fullWidth
                                  name="startWork"
                                  error={touched.startWork && Boolean(errors.startWork)}
                                  helperText={touched.startWork && errors.startWork}
                                  variant="outlined"
                                  margin="dense"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Field
                                  as={TextField}
                                  label={i18n.t("userModal.form.endWork")}
                                  type="time"
                                  ampm={"false"}
                                  inputRef={endWorkRef}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 600 }}
                                  fullWidth
                                  name="endWork"
                                  error={touched.endWork && Boolean(errors.endWork)}
                                  helperText={touched.endWork && errors.endWork}
                                  variant="outlined"
                                  margin="dense"
                                />
                              </Grid>
                            </>
                          )}
                        />
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Data de Nascimento"
                            type="date"
                            name="birthDate"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            helperText="Data de nascimento para notificações de aniversário"
                            onChange={(e) => {
                              const formattedDate = parseDateFromInput(e.target.value);
                              setFieldValue("birthDate", formattedDate);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.farewellMessage")}
                            type="farewellMessage"
                            multiline
                            rows={4}
                            fullWidth
                            name="farewellMessage"
                            error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                            helperText={touched.farewellMessage && errors.farewellMessage}
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                      </Grid>
                    </div>
                  </TabPanel>
                  <TabPanel
                    className={classes.tabPanel}
                    value={tab}
                    name={"permissions"}
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <>
                          {permissionCategories.map((category) => (
                            <div className={classes.permissionsCategory} key={category.title}>
                              <Typography className={classes.permissionsCategoryTitle}>
                                {category.title}
                              </Typography>
                              <Grid container spacing={2}>
                                {category.items.map((permission) =>
                                  renderPermissionItem(permission, values, setFieldValue)
                                )}
                              </Grid>
                            </div>
                          ))}
                        </>
                      )}
                    />
                  </TabPanel>
                </DialogContent>
              </Paper>
              <DialogActions className={classes.fixedActions}>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {userId
                    ? `${i18n.t("userModal.buttons.okEdit")}`
                    : `${i18n.t("userModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default UserModal;
